// The Upcoming Trips card on an Item's page. Always rendered (empty state
// shows help text) between the logbooks and the sub-equipment card.
//
// Read-only view of the parent's item model: trips live on `item.trips.items`
// (loaded upcoming-only, soonest first, by Database.listUpcomingTripsForItem).
// All mutations happen elsewhere -- Add creates a stub leg and opens its
// detail page, and rows open the trip-group page -- so the card just re-reads
// whatever the Item page loaded.
//
//   item      - the Item (carries trips in item.trips.items)
//   onSeeAll  - optional override for "See All". Defaults to this item's own
//               trips list, which is what the Trips page wants; the item page
//               overrides it to the cross-item Trips page instead.

import { useRootCrumbNavigate } from '../item/RootCrumb';
import Button from 'react-bootstrap/Button';
import { useStatusMessage, DataCard } from '../../components/index.js';
import { CardHeader } from '../../components/CardHeader.jsx';
import * as Database from '../../utilities/Database.mjs';
import { urlForTrip, urlForTrips, urlForTripGroup } from '../../fleet-shared/Urls.mjs';
import {
    groupTrips,
    isUpcomingTripGroup,
    sortTripGroups,
    groupWithStoredLegs,
} from '../../fleet-shared/TripGroups.mjs';
import { tripWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import TripGroupsTable from './TripGroupsTable.jsx';

// `placeIndex` comes from the PAGE (Item, AllTrips), not from a hook in here --
// see LogbookSummary for why. Absent, an unnamed trip's from-to name and the
// distance line carry the full place names, as they always did.
const TripsSummary = ({ item, placeIndex, onSeeAll }) => {
    const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();
    const navigate = useRootCrumbNavigate();

    // Edit the new stub on its detail page. No local-state insert: this page
    // unmounts on navigate and the Item page refetches its trips on return.
    function didAdd(savedTrip) {
      navigate(urlForTrip(item.id, savedTrip.id), { state: { item, trip: savedTrip } });
    }

    async function handleAdd() {
      await Database.createTripWithUI(item.id, showText, showError, hideStatus, didAdd);
    }

    function onAll() {
      if (onSeeAll) {
        onSeeAll();
        return;
      }
      navigate(urlForTrips(item.id));
    }

    function onDetails(group) {
      const stored = groupWithStoredLegs(group, item?.trips?.items ?? []);
      navigate(urlForTripGroup(item.id, group.key), { state: { item, group: stored } });
    }

    // Cap the card at the next few trips (grouped by name). The loader
    // (Database.listUpcomingTripsForItem) fetches a generous window of legs so
    // groups form completely; here we group, keep only upcoming ones, and show
    // the soonest few. "See All" surfaces whenever more groups exist.
    const numTripsToShow = 5;

    const Trips = () => {
        // Grouped from DISPLAY copies: a group's from/to come from its legs'
        // columns, which mirror the route as of the last save, so a place
        // renamed in /me/places since then is reachable only through the
        // waypoint's placeId. onDetails puts the stored legs back for the
        // handover (groupWithStoredLegs).
        const storedLegs = item?.trips?.items ?? [];
        const groups = sortTripGroups(
            groupTrips(
                storedLegs.map((leg) => tripWithResolvedEndpoints(leg, placeIndex)),
                { placeIndex }
            ).filter((g) => isUpcomingTripGroup(g)),
            'asc'
        );
        if (groups.length === 0) {
            return (
                <p className="text-center text-body-secondary m-0">
                    No upcoming trips. Add one to plan your next outing.
                </p>
            );
        }

        return (
            <>
                <TripGroupsTable groups={groups.slice(0, numTripsToShow)} placeIndex={placeIndex} onDetails={onDetails} />
                {groups.length > numTripsToShow && (
                    <div className="text-center mt-2">
                        <Button variant="primary" size="sm" onClick={onAll}>See All</Button>
                    </div>
                )}
            </>
        );
    }


    return (
        <div>
            <DataCard
              header=<CardHeader text='Upcoming Trips' addTapped={handleAdd} seeAllTapped={onAll}/>
              body={
                <div className="row justify-content-center">
                    <div className="text-center center mb-2">
                        {StatusMessage()}
                        <div className="logbook-entries">
                            <Trips />
                        </div>
                    </div>
                </div>
              }
            />
        </div>
    );
}

export default TripsSummary;
